import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Truck, TrendingDown, Route, Gauge, ChevronDown } from "lucide-react";
import { api } from "../../services/api";
import DalmiaDecisionCard from "../../components/dalmia/DalmiaDecisionCard";

function Mark({ shape, color }) { return <span className={`type-mark ${shape}`} style={{ "--mk": color }} />; }

const CATS = [
  { id: "fulfillment", label: "Fulfillment Risk", shape: "square", color: "#dc2626", icon: Truck },
  { id: "margin", label: "Margin Early Warning", shape: "circle", color: "#d97706", icon: TrendingDown },
  { id: "logistics", label: "Logistics Bottleneck", shape: "triangle", color: "#4f46e5", icon: Route },
  { id: "procurement", label: "Input-Cost Signal", shape: "diamond", color: "#0891b2", icon: Gauge },
];

export default function DalmiaDecisions() {
  const [summary, setSummary] = useState(null);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState(null);
  const [sel, setSel] = useState({ category: "", severity: "", region: "" });
  const [showBelow, setShowBelow] = useState(false);
  const [params] = useSearchParams();
  const nav = useNavigate();
  const openId = params.get("open");

  useEffect(() => {
    api.dalmiaSummary().then(setSummary);
    api.dalmiaDecisions().then(setData);
    api.dalmiaFilters().then(setFilters);
  }, []);

  const set = (k, v) => setSel((p) => ({ ...p, [k]: v }));
  const reset = () => setSel({ category: "", severity: "", region: "" });
  const active = Object.values(sel).some(Boolean);

  const shown = useMemo(() => {
    if (!data) return [];
    return data.top.filter((d) =>
      (!sel.category || d.category === sel.category) &&
      (!sel.severity || d.severity === sel.severity) &&
      (!sel.region || d.region === sel.region));
  }, [data, sel]);

  if (!summary || !data) return <div className="loading-page">Correlating Dalmia fulfillment & margin signals…</div>;

  return (
    <div className="insights-page" data-testid="dalmia-insights-page">
      <div className="vans-eyebrow" style={{ color: "#0f766e" }}>
        Dalmia Bharat · Evidence-backed decisions · {summary.today}
      </div>
      <h1 className="vans-title" style={{ margin: "4px 0 6px" }}>Explainable Insights</h1>
      <p className="vans-sub" style={{ maxWidth: 760, marginBottom: 14 }}>
        Every decision is a correlation of independent signals — SAP orders & inventory, the Suvidha dealer app,
        TMS logistics, plant MES/SCADA, Ariba procurement and the BI data-lake. Expand any card for the AI
        explanation, decomposable confidence, supply-chain flow and Section-22 fact-vs-inference breakdown.
      </p>

      <div className="vans-legend" style={{ marginBottom: 16 }}>
        {CATS.map((c) => <span key={c.id} className="vl-item"><Mark shape={c.shape} color={c.color} /> {c.label}</span>)}
      </div>

      {filters && (
        <div className="filter-bar" data-testid="dalmia-filter-bar">
          <FilterSelect label="Category" value={sel.category} onChange={(v) => set("category", v)} options={filters.categories.map((c) => ({ value: c.id, label: c.label }))} />
          <FilterSelect label="Severity" value={sel.severity} onChange={(v) => set("severity", v)} options={filters.severities.map((s) => ({ value: s, label: s }))} />
          <FilterSelect label="Region" value={sel.region} onChange={(v) => set("region", v)} options={filters.regions.map((r) => ({ value: r, label: r }))} />
          {active && <button className="btn-ghost sm" onClick={reset} data-testid="dalmia-clear-filters">Clear filters</button>}
          <span className="filter-count">{shown.length} decision{shown.length !== 1 ? "s" : ""}</span>
        </div>
      )}

      {shown.length === 0 ? (
        <div className="empty-state">No decisions match these filters.</div>
      ) : (
        <div className="decision-list">
          {shown.map((d) => <DalmiaDecisionCard key={d.id} d={d} defaultOpen={openId ? openId === d.id : d.rank === 1} />)}
        </div>
      )}

      {!active && (
        <section className="below-section">
          <button className="below-toggle" onClick={() => setShowBelow((v) => !v)}>
            <ChevronDown size={16} className={`chev ${showBelow ? "open" : ""}`} />
            Below today's cutoff — {data.below.length} more monitored (low corroboration)
          </button>
          {showBelow && (
            <div className="below-grid">
              {data.below.map((d) => (
                <div key={d.id} className="below-mini">
                  <div className="bm-top">
                    <span className="type-pill" style={{ background: d.category_meta.soft, color: d.category_meta.text }}>
                      <Mark shape={d.category_meta.shape} color={d.category_meta.color} /> {d.category_label}
                    </span>
                    <span className="bm-conf">score {d.score}</span>
                  </div>
                  <div className="bm-title">{d.entity_name}</div>
                  <div className="bm-head">{d.why_summary}</div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      <footer className="vans-foot">DotIQ × Dalmia Bharat — POC4 sandbox · synthetic/anonymized data · no automated plant or despatch control · human approval required</footer>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options }) {
  return (
    <label className="filter-select">
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">All</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}
